import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { CruiseLine } from "../components/CruiseLine";

/* One real post moving through the pipeline - the hero's proof (PRD 21).
   The landing page's example: a news feed surfaces a story, AutoCruise
   writes and renders it, the gate checks it, and it is scheduled.

   It advances a stage at a time on a loop. With reduced motion it shows the
   finished state and stays there. */
const STAGES = ["Listen", "Plan", "Create", "Check", "Approve", "Publish"];
const CHECKS = ["Caption complete", "No AI tells", "Image rendered", "Not a repeat"];
const STEP_MS = 1700;

const prefersReduced = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export const PostCard = ({ visible = true, compact = false }) => (
  <div className={`rounded-[10px] bg-[#1B1450] p-4 flex flex-col transition-opacity duration-base ease-ac ${
    visible ? "opacity-100" : "opacity-0"} ${compact ? "aspect-[4/5]" : "aspect-[4/5]"}`}>
    <span className="block h-1 w-8 rounded-full bg-[#F472B6]" />
    <p className="mt-3 font-display text-[17px] sm:text-[19px] font-semibold leading-tight text-white">
      Four-day weeks are quietly winning in startups
    </p>
    <p className="mt-auto text-[10px] text-[#A5B4FC]/80">Source: Signal Weekly · yourbrand.com</p>
  </div>
);

export default function Pipeline() {
  const [stage, setStage] = useState(prefersReduced() ? STAGES.length - 1 : 0);

  useEffect(() => {
    if (prefersReduced()) return undefined;
    const t = setInterval(() => setStage((s) => (s + 1) % (STAGES.length + 1)), STEP_MS);
    return () => clearInterval(t);
  }, []);

  // The extra tick past the last stage holds the finished state a moment.
  const at = Math.min(stage, STAGES.length - 1);
  const ticks = at < 3 ? 0 : at === 3 ? CHECKS.length - 1 : CHECKS.length;

  return (
    <div className="rounded-[10px] border border-ac-border bg-ac-surface p-4 sm:p-5" data-testid="hero-pipeline">
      <CruiseLine stages={STAGES} active={at} />

      <div className="mt-5 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4">
        <div className="space-y-2">
          <p className="mono-label text-ac-muted !text-[10px]">
            {at === 0 ? "New story · Signal Weekly" : at === 1 ? "Planned · Viral news card" : "Post preview"}
          </p>
          {at === 0 ? (
            <div className="rounded-[10px] border border-ac-border bg-ac-raised p-3 text-[12px] text-ac-text leading-snug">
              Four-day weeks are quietly winning in startups
              <span className="mt-2 block text-[10px] text-ac-muted">signalweekly.com · 4 min ago</span>
            </div>
          ) : at === 1 ? (
            <div className="rounded-[10px] border border-ac-border bg-ac-raised p-3 space-y-1.5 text-[11px] text-ac-muted">
              <p>Pillar · <span className="text-ac-text">Future of work</span></p>
              <p>Format · <span className="text-ac-text">Viral news card</span></p>
              <p>Repeat check · <span className="text-ac-text">nothing similar in 30 days</span></p>
            </div>
          ) : (
            <PostCard />
          )}
        </div>

        <div className="space-y-2">
          <p className="mono-label text-ac-muted !text-[10px]">Quality gate</p>
          <ul className="space-y-1.5">
            {CHECKS.map((c, i) => (
              <li key={c} className={`flex items-center gap-2 text-[12px] transition-colors duration-base ${
                i < ticks ? "text-ac-text" : "text-ac-muted/50"}`}>
                <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${
                  i < ticks ? "bg-[#4ADE80]/15 text-[#4ADE80]" : "border border-ac-border"}`}>
                  {i < ticks && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                </span>
                {c}
              </li>
            ))}
          </ul>
          <div className={`pt-2 transition-opacity duration-base ${at >= 3 ? "opacity-100" : "opacity-0"}`}>
            <p className="font-mono text-2xl text-ac-text leading-none">
              9.2<span className="text-sm text-ac-muted"> / 10</span>
            </p>
            <p className="mono-label text-ac-muted !text-[10px] mt-1">On-brand · bar 8.0</p>
          </div>
          <div className={`pt-1 transition-opacity duration-base ${at >= 4 ? "opacity-100" : "opacity-0"}`}>
            <span className="inline-block rounded-md border border-[#2C3170] bg-[#191C45] px-2 py-1 font-mono text-[11px] text-[#A5B4FC]">
              {at >= 5 ? "Scheduled · LinkedIn Tue 9:00" : "Approved · quality mode"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
